import {
  UtilityCopyProps,
  MainSection,
  Section,
  AnchorLink,
  Subsection,
} from "@bluebottlecoffee/design-system/components";
import { toHTML } from "@portabletext/to-html";
import {
  MainHeading as MainHeadingSchema,
  Section as SectionSchema,
  UtilityCopy as UtilityCopySchema,
  Subsection as SubsectionSchema,
  LocalizedBlock,
  ImageWithAltText,
} from "../sanity-schema";
import { toLinkProps } from "./link";
import { toImageProps } from "./image";
import { toTableProps } from "./table";
import { ElementOf } from "../utils";

/**
 * Transform to an `AnchorLink` type.
 *
 * @param {*} data
 * @param {string} lang
 * @return {*}  {AnchorLink}
 */
function toAnchorLinkProps(data: any, lang: string): AnchorLink {
  return {
    linkText: data.linkText[lang],
    headingText: data.headingText[lang],
  };
}

/**
 * Transform `MainHeading` schema into a `MainSection` type.
 *
 * @param {MainHeadingSchema} data
 * @param {string} region
 * @param {string} lang
 * @return {*}  {MainSection}
 */
function toMainSectionProps(
  data: MainHeadingSchema,
  region: string,
  lang: string,
): MainSection {
  return {
    heading: toAnchorLinkProps(data.heading, lang),
    detail: data.detail && toHTML(data.detail[lang]),
  };
}

/**
 * Transform the details of a Subsection into the appropriate type, depending
 * on what `_type` the subsection detail is. For example, return
 * `ImageProps` or a portable text block.
 *
 * @param {*} module
 * @param {string} region
 * @param {string} lang
 * @return {*}  {*}
 */
function toSubsectionDetailsProps(
  module: ElementOf<SubsectionSchema["details"]>,
  lang: string,
): ElementOf<Subsection["details"]> {
  switch (module._type) {
    case "imageWithAltText":
      return {
        _type: "image",
        ...toImageProps(module as ImageWithAltText, lang),
      };
    case "localizedBlock":
      return toHTML(module[lang] as LocalizedBlock);
    case "table":
      return {
        _type: "table",
        ...toTableProps(module, lang),
      };
    default: {
      throw Error(
        `Module did not have a UtilityCopy#toSubsectionDetailsProps transformer.`,
      );
    }
  }
}

/**
 * Transform `Subsection` schema into a `Subsection` type.
 *
 * @param {SubsectionSchema} data
 * @param {string} region
 * @param {string} lang
 * @return {*}  {Subsection}
 */
function toSubSectionProps(data: SubsectionSchema, lang: string): Subsection {
  return {
    subheading: data.subheading && data.subheading[lang],
    ...(data.details && {
      details: data.details.map((detail) =>
        toSubsectionDetailsProps(detail, lang),
      ),
    }),
  };
}

/**
 * Transform `Section` schema into a `Section` type.
 *
 * @param {SectionSchema} data
 * @param {string} region
 * @param {string} lang
 * @return {*}  {Section}
 */
function toSectionProps(data: SectionSchema, lang: string): Section {
  return {
    heading: toAnchorLinkProps(data.heading, lang),
    ...(data.subsections && {
      subsections: data.subsections.map((subsection) =>
        toSubSectionProps(subsection, lang),
      ),
    }),
  };
}

/**
 * Transform `UtilityCopy` schema into `UtilityCopyProps` type, but omit
 * `copyProps` - which will come from a translationGroup.
 *
 * @export
 * @param {UtilityCopySchema} data
 * @param {string} region
 * @param {string} lang
 * @return {*}  {Omit<UtilityCopyProps, "copyProps">}
 */
export function toUtilityCopyProps(
  data: UtilityCopySchema,
  region: string,
  lang: string,
): Omit<UtilityCopyProps, "copyProps"> {
  return {
    lastModified: new Date(data.lastModified),
    mainHeading: toMainSectionProps(data.mainHeading, region, lang),
    ...(data.pageRefLinks && {
      pageRefLinks: data.pageRefLinks.map((link) =>
        toLinkProps(link, region, lang),
      ),
    }),
    ...(data.sections && {
      sections: data.sections.map((section) => toSectionProps(section, lang)),
    }),
  };
}
